import type { CustomPaletteKey } from "@citrine/theme";

import type { AuditInfo } from "../../common";
import type { ExecutionCandidate } from "../../execution-candidate";
import { isOfType } from "../../utils";

interface Join<T> {
  operation: "and" | "or";
  terms: T[];
}

type Range =
  | {
      min: number;
      max?: number;
    }
  | {
      min?: number;
      max: number;
    };

export type DescriptorFilter =
  | RealDescriptorFilter
  | CategoricalDescriptorFilter;

export type RealDescriptorFilter = {
  type: "RealFilter";
  descriptor_key: string;
  units: string;
} & Range;

// Values would always be OR - no $and or $or required
export type CategoricalDescriptorFilter = {
  type: "CategoricalFilter";
  descriptor_key: string;
  values: string[];
  readonly exclude: boolean; // deprecated
} & Probability;

export type Probability =
  | {
      most_likely: true;
      probability?: undefined;
    }
  | {
      most_likely?: undefined | false;
      probability: {
        /**
         * Deprecating `min` (between 0-1) in favor of `minPercent` (0-100)
         * */
        readonly min?: number;
        min_percent: number;
      };
    };

export const FORMULATION_OPERATION = "or" as const;
export type FormulationFilter = {
  type: "FormulationFilter";
  formulation: string;
  terms: string[];
} & Range;

export interface IngredientFilter extends Join<FormulationFilter> {
  type: "IngredientFilter";
}

export type IngredientCountSubfilter = {
  type: "IngredientCountSubfilter";
  formulation: string;
} & Range;

export interface IngredientCountFilter extends Join<IngredientCountSubfilter> {
  type: "IngredientCountFilter";
}

export interface LabelFilter extends Join<FormulationFilter> {
  type: "LabelFilter";
}

export interface ProcessFilter<
  Filter extends DescriptorFilter = DescriptorFilter,
> extends Join<Filter> {
  type: "ProcessFilter";
}

export interface PerformanceFilter<
  Filter extends DescriptorFilter = DescriptorFilter,
> extends Join<Filter> {
  type: "PerformanceFilter";
}

export type SubFilter =
  | DescriptorFilter
  | FormulationFilter
  | IngredientCountSubfilter;

export const isDescriptorFilter = isOfType<DescriptorFilter, SubFilter>(
  "RealFilter",
  "CategoricalFilter"
);

export const isRealDescriptorFilter = isOfType<RealDescriptorFilter, SubFilter>(
  "RealFilter"
);

export const isCategoricalDescriptorFilter = isOfType<
  CategoricalDescriptorFilter,
  SubFilter
>("CategoricalFilter");

export const isFormulationFilter = isOfType<FormulationFilter, SubFilter>(
  "FormulationFilter"
);

export type CandidateGroupFilter =
  | ProcessFilter
  | PerformanceFilter
  | IngredientFilter
  | IngredientCountFilter
  | LabelFilter;

export const isIngredientFilter = isOfType<
  IngredientFilter,
  CandidateGroupFilter
>("IngredientFilter");

export const isIngredientCountFilter = isOfType<
  IngredientCountFilter,
  CandidateGroupFilter
>("IngredientCountFilter");

export const isLabelFilter = isOfType<LabelFilter, CandidateGroupFilter>(
  "LabelFilter"
);

export const isPerformanceFilter = isOfType<
  PerformanceFilter,
  CandidateGroupFilter
>("PerformanceFilter");

export const isProcessFilter = isOfType<ProcessFilter, CandidateGroupFilter>(
  "ProcessFilter"
);

export const FILTER_COLORS = [
  "dataViz01",
  "dataViz02",
  "dataViz03",
  "dataViz04",
  "dataViz05",
  "dataViz06",
] as const satisfies (CustomPaletteKey & `dataViz${number}`)[];
type FilterColor = (typeof FILTER_COLORS)[number];

export interface FilterGroupAddenda {
  id: string;
  color: FilterColor;
  title: string;
  note?: string;
}

export interface FilterGroup
  extends Join<CandidateGroupFilter>, FilterGroupAddenda {
  newFilterGroup?: boolean;
}

export interface FilterGroups {
  filter_groups: FilterGroup[];
  updated?: AuditInfo;
}

interface NewFilterGroup
  extends Join<Partial<CandidateGroupFilter>>, FilterGroupAddenda {
  newFilterGroup: true;
}

export type ScratchPadFilterGroup = FilterGroup | NewFilterGroup;

export type FilterFunction = (candidate: ExecutionCandidate) => boolean;
